import type { FC, ReactNode } from 'react'
import type { SessionUser } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import {
    ChevronDown,
    Images,
    LayoutGrid,
    LogOut,
    Menu,
    Palette,
    Plus
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { api, errorMessage } from '@/lib/client/api'
import { buttonVariants } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'
import { Logo } from '@/components/generals/logo'

interface AppHeaderProps {
    user: SessionUser | null
}

const NAV = [
    { href: '/posts', label: 'Posts', icon: Images },
    { href: '/feeds', label: 'Feeds', icon: LayoutGrid },
    { href: '/brand', label: 'Brand kit', icon: Palette }
]

export const AppHeader: FC<AppHeaderProps> = ({ user }): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)

    const logOut = async (): Promise<void> => {
        try {
            await api('/api/auth/logout', { method: 'POST' })
            await router.push('/')
        }
        catch (error) {
            toast.error(errorMessage(error))
        }
    }

    const initial = (user?.name || user?.email || 'F').trim().charAt(0).toUpperCase()

    return (
        <header className='sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur'>
            <div className='mx-auto flex h-14 max-w-[1600px] items-center gap-4 px-4 sm:px-6'>
                <Logo href='/posts' className='text-lg' />

                <nav aria-label='Studio' className='ml-4 hidden items-center gap-1 md:flex'>
                    {NAV.map(item => (
                        <Link
                            key={item.href}
                            href={item.href}
                            aria-current={isActive(item.href) ? 'page' : undefined}
                            className={cn(
                                'inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                                isActive(item.href) ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            <item.icon className='size-4' />
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <div className='ml-auto flex items-center gap-2'>
                    <Link href='/create' className={cn(buttonVariants(), 'hidden h-9 rounded-full px-4 sm:inline-flex')}>
                        <Plus className='size-4' />
                        New post
                    </Link>

                    {user && (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                className='hidden items-center gap-1.5 rounded-full border border-border py-1 pr-2 pl-1 text-sm hover:bg-muted md:inline-flex'
                                aria-label='Account menu'
                            >
                                <span className='flex size-7 items-center justify-center rounded-full bg-ink text-xs font-semibold text-paper'>
                                    {initial}
                                </span>
                                <ChevronDown className='size-4 text-muted-foreground' />
                            </DropdownMenuTrigger>

                            <DropdownMenuContent align='end' className='w-60'>
                                <DropdownMenuGroup>
                                    <DropdownMenuLabel className='truncate'>
                                        {user.email}
                                    </DropdownMenuLabel>
                                </DropdownMenuGroup>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => router.push('/brand')}>
                                    <Palette className='size-4' />
                                    Brand kit and account
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => void logOut()}>
                                    <LogOut className='size-4' />
                                    Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}

                    <button
                        type='button'
                        className='inline-flex size-9 items-center justify-center rounded-full border border-border md:hidden'
                        onClick={() => setOpen(true)}
                        aria-label='Open menu'
                    >
                        <Menu className='size-5' />
                    </button>
                </div>
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side='right' className='w-80'>
                    <SheetHeader>
                        <SheetTitle>Studio</SheetTitle>
                    </SheetHeader>

                    <nav aria-label='Studio mobile' className='flex flex-col gap-1 px-4'>
                        <Link href='/create' onClick={() => setOpen(false)} className='flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium hover:bg-muted'>
                            <Plus className='size-5' />
                            New post
                        </Link>
                        {NAV.map(item => (
                            <Link key={item.href} href={item.href} onClick={() => setOpen(false)} className='flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium hover:bg-muted'>
                                <item.icon className='size-5' />
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    {user && (
                        <div className='mt-auto flex flex-col gap-3 border-t border-border p-4'>
                            <p className='truncate text-sm text-muted-foreground'>
                                {user.email}
                            </p>
                            <button type='button' onClick={() => void logOut()} className={cn(buttonVariants({ variant: 'outline' }), 'h-11 rounded-full')}>
                                <LogOut className='size-4' />
                                Log out
                            </button>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </header>
    )
}
